import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useDialog } from '../../../hooks/use-dialog';
import { useSharedView } from '../../../hooks/use-shared-view';
import { GeodataSetupContext, GeodataSetupState } from '../../../state/geodata-setup-state';
import { Button } from '../../button/button';
import { CheckBox } from '../../check-box/check-box';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { GeodataSetup } from './geodata-setup';
import { GeodataSetupFooterButtons } from './geodata-setup-footer-buttons';

type Props = {
  onCloseDialog?: () => void;
};

export const GeodataSetupButton = (props: Props = { onCloseDialog: undefined }) => {
  const { t } = useTranslation();
  const dialog = useDialog();

  const buttonLabel = t('setupDialog.title', { ns: 'cloudProcessing' });
  const content = <span aria-hidden={true}>{buttonLabel}</span>;

  const closeDialog = useCallback(() => {
    props.onCloseDialog?.();
    dialog.hide();
  }, [dialog, props]);

  return (
    <>
      <Button
        aria-label={buttonLabel}
        color="primary"
        leftIcon={{ icon: ['fad', 'plus'] }}
        noWrap={true}
        variant="contained"
        onClick={() => dialog.show()}
      >
        {content}
      </Button>
      {dialog.render(
        <Dialog
          context={(children) => (
            <GeodataSetupState closeDialog={closeDialog}>{children}</GeodataSetupState>
          )}
          maxHeight="expand"
          maxWidth="90%"
          onClose={dialog.hide}
        >
          {{
            header: t('setupDialog.title', { ns: 'cloudProcessing' }),
            content: <GeodatSetupDialogContent />,
            footer: {
              left: <GeodataSetupFinishInfo />,
              right: <GeodataSetupFooterButtons />,
            },
          }}
        </Dialog>,
      )}
    </>
  );
};

const GeodatSetupDialogContent = () => {
  const context = useContext(GeodataSetupContext);
  return (
    <Component width={context?.page !== 'processType' ? '65vh' : undefined}>
      <GeodataSetup height="60vh" />
    </Component>
  );
};

const GeodataSetupFinishInfo = () => {
  const { t } = useTranslation('cloudProcessing');
  const context = useContext(GeodataSetupContext);
  const { isSharedView } = useSharedView();

  if (context?.page !== 'finish' || context.isFinishComplete || isSharedView) {
    return null;
  }

  return (
    <InfoBox color="blue">
      <InfoBoxContent>
        <span>{t('geodataSetup.finish.keepWindowOpen', { ns: 'cloudProcessing' })}</span>
        <CheckBox
          checked={context.abortConfirmed}
          onChange={(e) => context.setAbortConfirmed(e.target.checked)}
        >
          {t('geodataSetup.finish.abortConfirmCheckbox', { ns: 'cloudProcessing' })}
        </CheckBox>
      </InfoBoxContent>
    </InfoBox>
  );
};

const Component = styled.div<{ width?: string }>`
  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `}
`;

const InfoBoxContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2em;
  white-space: nowrap;
`;
